// The bell in Flow's header (Stephanie 2026-10-12). Every alert a person gets
// (first one: "Proof sent — PO #…") is kept here, newest first, with an unread
// count. From the same menu they can turn on pop-up alerts for this phone or
// computer (Web Push through /flow-sw.js). Hidden until Jake runs
// scripts/push-notifications.ts (GET /notifications answers available: false).
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";
import { cn, timeAgo } from "@/lib/utils";

interface Alert {
  id: string;
  kind: string;
  title: string;
  body: string;
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

const KEY = ["notifications"] as const;

/** "BEl6…" (base64url) → the bytes PushManager.subscribe wants. */
function keyBytes(b64: string): Uint8Array {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

const pushSupported = () => typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

type DeviceState = "unsupported" | "off" | "on" | "blocked" | "unavailable";

function useDeviceAlerts() {
  const [state, setState] = useState<DeviceState>("unavailable");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const keyQ = useQuery({ queryKey: ["push", "public-key"], queryFn: () => api<{ key: string | null }>("/push/public-key"), staleTime: 3_600_000 });

  useEffect(() => {
    if (!pushSupported()) return setState("unsupported");
    if (!keyQ.data?.key) return setState("unavailable");
    if (Notification.permission === "denied") return setState("blocked");
    void navigator.serviceWorker.getRegistration("/").then(async (reg) => {
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      setState(sub ? "on" : "off");
    });
  }, [keyQ.data?.key]);

  const turnOn = async () => {
    const key = keyQ.data?.key;
    if (!key) return;
    setBusy(true);
    setError(null);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setState(perm === "denied" ? "blocked" : "off");
        return;
      }
      const reg = await navigator.serviceWorker.register("/flow-sw.js", { scope: "/" });
      await navigator.serviceWorker.ready;
      const sub =
        (await reg.pushManager.getSubscription()) ??
        (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) }));
      await api("/push/subscribe", { method: "POST", body: JSON.stringify(sub.toJSON()) });
      setState("on");
    } catch {
      setError("Couldn't turn on alerts on this device. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration("/");
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await api("/push/unsubscribe", { method: "POST", body: JSON.stringify({ endpoint: sub.endpoint }) });
        await sub.unsubscribe();
      }
      setState("off");
    } finally {
      setBusy(false);
    }
  };

  return { state, busy, error, turnOn, turnOff };
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: KEY,
    queryFn: () => api<{ available: boolean; unread: number; items: Alert[] }>("/notifications"),
    refetchInterval: 60_000,
  });
  const markRead = useMutation({
    mutationFn: (id?: string) => api("/notifications/read", { method: "POST", body: JSON.stringify(id ? { id } : {}) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
  const device = useDeviceAlerts();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!q.data?.available) return null;
  const { unread, items } = q.data;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={unread > 0 ? `Alerts, ${unread} unread` : "Alerts"}
        aria-expanded={open}
        className="relative flex h-10 w-10 items-center justify-center rounded-full text-ink hover:bg-canvas"
      >
        <Bell className="h-5 w-5" aria-hidden />
        {unread > 0 && (
          <span className="absolute right-1 top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-risk px-1 text-[11px] font-semibold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-12 z-30 w-[360px] max-w-[calc(100vw-2rem)] rounded-md border border-rule bg-white shadow-lg" role="menu" aria-label="Alerts">
          <div className="flex items-center justify-between border-b border-rule px-4 py-3">
            <p className="text-sm font-semibold text-ink">Alerts</p>
            {unread > 0 && (
              <button type="button" onClick={() => markRead.mutate(undefined)} className="text-xs text-muted underline underline-offset-2 hover:text-ink">
                Mark all read
              </button>
            )}
          </div>
          <ul className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <li className="px-4 py-8 text-center text-sm text-muted">No alerts yet.</li>
            ) : (
              items.map((a) => (
                <li key={a.id} className="border-b border-[#eeeeea] last:border-0">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      if (!a.readAt) markRead.mutate(a.id);
                      setOpen(false);
                      if (a.link) navigate(a.link);
                    }}
                    className={cn("flex w-full gap-3 px-4 py-3 text-left hover:bg-canvas", !a.readAt && "bg-[#f7f7f4]")}
                  >
                    <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", a.readAt ? "bg-transparent" : "bg-risk")} aria-hidden />
                    <span className="min-w-0">
                      <span className={cn("block text-sm text-ink", !a.readAt && "font-semibold")}>{a.title}</span>
                      <span className="block truncate text-[13px] text-muted">{a.body}</span>
                      <span className="block text-[11px] text-muted">{timeAgo(a.createdAt)}</span>
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
          {device.state !== "unavailable" && device.state !== "unsupported" && (
            <div className="border-t border-rule px-4 py-3 text-[13px]">
              {device.state === "on" ? (
                <p className="flex items-center justify-between gap-2 text-muted">
                  Pop-up alerts are on for this device.
                  <button type="button" disabled={device.busy} onClick={() => void device.turnOff()} className="text-ink underline underline-offset-2">
                    Turn off
                  </button>
                </p>
              ) : device.state === "blocked" ? (
                <p className="text-muted">Alerts are blocked in this browser's settings. Allow notifications for Flow there to turn them on.</p>
              ) : (
                <button
                  type="button"
                  disabled={device.busy}
                  onClick={() => void device.turnOn()}
                  className="w-full rounded-md bg-ink px-3 py-2 text-sm font-medium text-white hover:bg-ink-700 disabled:opacity-60"
                >
                  Turn on pop-up alerts on this device
                </button>
              )}
              {device.error && <p role="alert" className="mt-2 text-risk">{device.error}</p>}
            </div>
          )}
          {device.state === "unsupported" && (
            <p className="border-t border-rule px-4 py-3 text-[12px] text-muted">
              On an iPhone, add Flow to your Home Screen (Share → Add to Home Screen) and open it from there to get pop-up alerts.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
